import type { ReactNode } from 'react';
import { Label } from '@immo/ui-kit';

/**
 * The operator app's one field-with-error-slot (IMM-844.1).
 *
 * Four byte-equivalent copies of this used to live inside the pages that
 * needed it — `FormField` in `pages/api-keys/new.tsx` and
 * `pages/mcp-access/new.tsx`, `FieldRow` in `pages/admins/new.tsx` and
 * `pages/workspaces/new.tsx` — and none of them gave the error `<p>` any ARIA
 * semantics. The render-prop `errorId` wiring made each message *reachable*
 * from its control via `aria-describedby`; it never made it *spoken* when it
 * arrived, so fifteen operator fields refused a submit in silence (BL-117).
 *
 * `role="alert"` rather than `aria-live="polite"`: every one of those fields is
 * submit-triggered, a refusal is assertive by nature and must interrupt, and
 * `role="alert"` implies `aria-live="assertive"` so no second attribute is
 * needed. The role goes on the *conditional* `<p>`, never on the wrapper — an
 * element carrying it is announced when it enters the DOM, so a permanently
 * mounted wrapper would announce on every render instead of on the refusal.
 *
 * `aria-describedby` and `role="alert"` are complementary, not alternatives:
 * the association is what lets a user navigate back to the message, the role is
 * what tells them it appeared. Both stay.
 *
 * Deliberately narrower than the customer app's `components/form/form-field.tsx`:
 * that one also has a `helper` branch. If a helper branch is ever added here
 * it must NOT carry a role — hint text that
 * interrupts on every render is worse than silence, and
 * `__tests__/form-field.test.tsx` pins that.
 */
type FormFieldProps = {
  readonly id: string;
  readonly label: string;
  readonly required?: boolean;
  readonly error: string | null;
  readonly children: (errorId: string | undefined) => ReactNode;
};

export function FormField({ id, label, required, error, children }: FormFieldProps) {
  const errorId = `${id}-error`;

  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      {/* Keep decoration outside the label so exact label queries retain their name. */}
      {required === true && (
        <span aria-hidden="true" className="ml-0.5 text-danger-600">
          *
        </span>
      )}
      {children(error !== null ? errorId : undefined)}
      {error !== null && (
        <p id={errorId} role="alert" className="text-sm text-danger-600">
          {error}
        </p>
      )}
    </div>
  );
}
